import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { getMeta } from 'constants/meta';
import Head from 'components/Head';

const Gdpr = ({ location }) => {
  const { title, description } = getMeta(location.pathname);
  return (
    <Fragment>
      <Head title={title} description={description} />
      <section className="section section_gdpr">
        <div className="wrapper">
          <div className="row">
            <div className="col col-10 offset-1 col-tablet-12 offset-tablet-0">
              <div className="section__header-wrap _mb-66 _mb-phone-36">
                <h2 className="title title_size_m _mb-18 section__main-title _mb-phone-10">
                  GDPR Compliance Statement
                </h2>
                <h5 className="text section__sub-text">
                  Last modified: 23 October 2018
                </h5>
              </div>
              <div>
                <h5 className="title section__title _mb-17">Introduction</h5>
                <p className="text section__text _mb-23">
                  This GDPR compliance statement provides information about the
                  actions we take to ensure that we comply with the General Data
                  Protection Regulation (GDPR). Specifically, it relates to how
                  we handle, store and process the personal data of our
                  customers and our customers' personnel.
                </p>
                <h5 className="title section__title _mb-17">
                  What is the GDPR?
                </h5>
                <p className="text section__text _mb-13">
                  The GDPR regulates the collection, storage and use of personal
                  data across the European Union (EU). It also covers some
                  activities outside the EU.
                </p>
                <p className="text section__text _mb-13">
                  As well as promoting the protection of personal data, the GDPR
                  harmonises the standards of protection that apply in EU member
                  states.
                </p>
                <p className="text section__text _mb-23">
                  It came into force on 25 May 2018.
                </p>
                <h5 className="title section__title _mb-17">GDPR principles</h5>
                <p className="text section__text _mb-13">
                  The principles of the GDPR, embodied in the detailed
                  regulations, are as follows.
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text text list__item section__text _mb-7">
                    Personal data must be processed lawfully, fairly and in a
                    transparent manner in relation to the data subject.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Personal data must be collected for specified, explicit and
                    legitimate purposes and not further processed in a manner
                    that is incompatible with those purposes.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Personal data must be adequate, relevant and limited to what
                    is necessary in relation to the purposes for which they are
                    processed.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Personal data must be accurate and, where necessary, kept up
                    to date; every reasonable step must be taken to ensure that
                    personal data that are inaccurate, having regard to the
                    purposes for which they are processed, are erased or
                    rectified without delay.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Personal data must be kept in a form which permits
                    identification of data subjects for no longer than is
                    necessary for the purposes for which the personal data are
                    processed.
                  </li>
                  <li className="text list__item section__text">
                    Personal data must be processed in a manner that ensures
                    appropriate security of the personal data, including
                    protection against unauthorised or unlawful processing and
                    against accidental loss, destruction or damage, using
                    appropriate technical or organisational measures.
                  </li>
                </ul>
                <p className="text section__text _mb-23">
                  When we process your personal data, we comply with letter of
                  the GDPR and abide by the spirit of the principles.
                </p>
                <h5 className="title section__title _mb-17">
                  How the GDPR affects us
                </h5>
                <p className="text section__text _mb-13">
                  The GDPR distinguishes between two different roles that
                  organisations can take: "controllers" and "processors".
                </p>
                <p className="text section__text _mb-13">
                  A controller is a natural or legal person, public authority,
                  agency or other body which, alone or jointly with others,
                  determines the purposes and means of the processing of
                  personal data. A processor is a natural or legal person, etc,
                  which processes personal data on behalf of a controller.
                </p>
                <p className="text section__text _mb-13">
                  Confusingly, both controllers and processors may "process"
                  personal data; the fact that an organisation processes
                  personal data does not make it a processor. The critical
                  question is: who determines the purposes (and, less
                  importantly, means) of processing?
                </p>
                <p className="text section__text _mb-13">
                  The legal obligations which apply to an organisation depend
                  upon the role taken by the organisation.
                </p>
                <p className="text section__text _mb-13">
                  We act as a processor with respect to most of the personal
                  data included in the databases of your Siteplus websites. To
                  the extent that we act as a processor of your personal data,
                  we are subject to the GDPR-compliant data processing clauses
                  in our{' '}
                  <Link
                    to="/legal/terms"
                    className="link link_blue section__link"
                  >
                    terms and conditions of service
                  </Link>
                  .
                </p>
                <p className="text section__text _mb-23">
                  We act as a controller with respect to all the other personal
                  data that we process, including your personal data stored in
                  our customer relationship management, marketing, support and
                  accounting databases. See our{' '}
                  <Link
                    to="/legal/privacy"
                    className="link link_blue section__link"
                  >
                    privacy and cookies policy
                  </Link>{' '}
                  – which reflects the disclosure requirements of the GDPR – for
                  specific information about how we use this personal data.
                </p>
                <h5 className="title section__title _mb-17">
                  Actions taken to ensure GDPR compliance
                </h5>
                <p className="text section__text _mb-13">
                  We have undertaken a review of our personal data processing
                  activities. As a result of the review, we improved the
                  strategies and polices we use in relation to the processing of
                  personal data.
                </p>
                <p className="text section__text _mb-13">
                  Measures we take or have taken with respect to data protection
                  include the following.
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    Updating the processes that we use to get consents and to
                    maintain consents for marketing communications.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Improving the technical and organisational measures that we
                    use to protect your personal data (see below for more
                    information on this).
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Ensuring that privacy is built into our services from the
                    ground up (known as privacy by design).
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Prohibiting the transfer of personal data to any place
                    outside the European Economic Area, unless appropriate
                    safeguards are in place or transfer is otherwise permissible
                    under the GDPR.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Limiting the data that we collect, deleting old personal
                    data and ensuring that we do not store data any longer than
                    necessary.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Auditing and reviewing existing sub-processors of personal
                    data, to ensure that they comply with the GDPR.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Ensuring that contracts with sub-processors of personal data
                    comply with the requirements of the GDPR.
                  </li>
                  <li className="text list__item section__text">
                    Enabling the exercise of data subject rights, including the
                    right of access to personal data and the right to be
                    forgotten.
                  </li>
                </ul>
                <p className="text section__text _mb-23">
                  We will continue to review our strategies and policies to
                  ensure continuing compliance as the law changes and data
                  processing technologies progress.
                </p>
                <h5 className="title section__title _mb-17">
                  Security measures
                </h5>
                <p className="text section__text _mb-13">
                  We are always working to improve the security of our systems,
                  to ensure the confidentiality, availability and integrity of
                  your personal data.
                </p>
                <p className="text section__text _mb-13">
                  We use the following physical and organisational security
                  measures:
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    Visitor logs
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Asset management logs
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Physical access controls, including lockable doors
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Paperless offices
                  </li>
                  <li className="text list__item section__text _mb-7">
                    CCTV monitoring of access routes
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Burglar and fire alarm systems, with 24/7 monitoring and
                    response
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Security guard patrols
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Developer security training
                  </li>
                  <li className="text list__item section__text">
                    Peer review of code changes
                  </li>
                </ul>
                <p className="text section__text _mb-13">
                  We use a wide range of technological security measures,
                  including:
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    Secure access methods, including using VPN and SSH
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Multi-factor authentication where appropriate
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Minimum password standards, with expiration periods
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Encryption in transit (using secure TLS protocols)
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Encryption at rest
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Firewalls / traffic screening
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Strictly limited user rights
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Regular vulnerability scans and penetration testing
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Critical software patches applied on a priority basis
                  </li>
                  <li className="text list__item section__text">
                    Separated development, testing and production environments
                  </li>
                </ul>
                <p className="text section__text _mb-23">
                  We and our payment services providers comply with the PCI-DSS
                  standard.
                </p>
                <h5 className="title section__title _mb-17">
                  Features to facilitate your compliance
                </h5>
                <p className="text section__text _mb-13">
                  The GDPR applies to many of our customers. In addition to
                  ensuring that we comply with the GDPR, we have taken the
                  following measures to help our customers to comply.
                </p>
                <ul className="list list_disc _mb-23">
                  <li className="text list__item section__text _mb-7">
                    Providing GDPR-friendly legal documentation for you to use
                    on your Siteplus websites.
                  </li>
                  <li className="text list__item section__text _mb-7">
                    Ensuring our contracts with you include the clauses that
                    need to be there for the purposes of your own GDPR
                    compliance.
                  </li>
                  <li className="text list__item section__text">
                    Mechanisms to help individuals exercise their rights under
                    the GDPR are in development.
                  </li>
                </ul>
                <h5 className="title section__title _mb-17">Contact</h5>
                <p className="text section__text">
                  If you have any questions about our approach to GDPR
                  compliance,{' '}
                  <a
                    className="link link_blue section__link"
                    href="mailto:sales@siteplus.com"
                  >
                    please do get in touch
                  </a>
                  {/* <Link to="/contact" className="link link_blue section__link">
                    please do get in touch
                  </Link> */}
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default Gdpr;
